/**
 * The figures the detail popups show, as plain descriptors: {key, label, value, percent | null, explain, how}. `percent` draws
 * a meter; `explain` is the one-line plain-English reading; `how` is the tooltip saying how it is computed. Pure functions over
 * the server's `diagnosis` block (GET /sessions/{id}/context) and a history cause ({cases, percent}). Every field may be absent.
 */
import { STATS_TEXT as S, TRACK_TEXT as K } from "./catalogStatsText";

const PERCENT_MAX = 100;

export const clampPercent = (value) => {
  if (value === null || value === undefined || value === "" || typeof value === "boolean") return null;
  const n = Math.round(Number(value));
  return Number.isFinite(n) ? Math.max(0, Math.min(PERCENT_MAX, n)) : null;
};
const count = (value) => (Number.isInteger(value) && value >= 0 ? value : 0);
const isInt = (value) => Number.isInteger(value);

const tile = (key, label, value, explain, how, percent = null) => ({ key, label, value, explain, how, percent });

/** The tiles of a diagnosis: likelihood at the end, rank, lead, steps, messages, facts, causes ruled out, failed fixes. */
export function diagnosisTiles(d) {
  if (!d || typeof d !== "object") return [];
  const tiles = [];
  const percent = clampPercent(d.learned_percent);
  if (d.learned_cause_key) {
    tiles.push(percent === null
      ? tile("likelihood", S.learnedPercent.label, "—", S.learnedPercent.missing, S.learnedPercent.how)
      : tile("likelihood", S.learnedPercent.label, `${percent}%`, S.learnedPercent.explain(percent), S.learnedPercent.how, percent));
  }
  const considered = count(d.causes_considered);
  if (isInt(d.learned_rank) && d.learned_rank >= 1 && considered) {
    tiles.push(tile("rank", S.rank.label, S.rank.value(d.learned_rank, considered), S.rank.explain(d.learned_rank, considered), S.rank.how));
  }
  if (isInt(d.margin_points) && considered > 1) {
    tiles.push(tile("margin", S.margin.label, S.margin.value(d.margin_points), S.margin.explain(d.margin_points), S.margin.how));
  }
  const steps = count(d.steps);
  const answered = Math.min(count(d.answered), steps);
  tiles.push(tile("steps", S.steps.label, S.steps.value(steps, answered), S.steps.explain(steps, answered), S.steps.how));
  if (isInt(d.turns) && d.turns >= 0) tiles.push(tile("turns", S.turns.label, `${d.turns}`, S.turns.explain(d.turns), S.turns.how));
  const confirmed = count(d.facts_confirmed);
  const denied = count(d.facts_denied);
  tiles.push(tile("facts", S.facts.label, S.facts.value(confirmed, denied), S.facts.explain(confirmed, denied), S.facts.how));
  const ruledOut = Array.isArray(d.ruled_out) ? d.ruled_out.length : 0;
  if (ruledOut) tiles.push(tile("ruledOut", S.ruledOut.label, `${ruledOut}`, S.ruledOut.explain(ruledOut), S.ruledOut.how));
  const failed = count(d.failed_proposals);
  if (failed) tiles.push(tile("failed", S.failed.label, `${failed}`, S.failed.explain(failed), S.failed.how));
  return tiles;
}

/** The leading causes as meter rows: {key, name, percent, learned}, highest first; malformed rows are dropped. */
export function causeShares(d) {
  const causes = d && Array.isArray(d.causes) ? d.causes : [];
  return causes
    .filter((c) => c && typeof c === "object" && c.cause_key)
    .map((c) => ({ key: c.cause_key, name: c.name || c.cause_key, percent: clampPercent(c.percent) ?? 0, learned: Boolean(c.learned) }))
    .sort((a, b) => b.percent - a.percent);
}

/** The ruled-out causes: {key, name, reason}. */
export const ruledOutCauses = (d) => (d && Array.isArray(d.ruled_out) ? d.ruled_out : [])
  .filter((c) => c && typeof c === "object" && c.cause_key)
  .map((c) => ({ key: c.cause_key, name: c.name || c.cause_key, reason: c.reason || "" }));

/** The steps of the timeline: {key, text, result}, in the stored order. */
export const timelineSteps = (d) => (d && Array.isArray(d.timeline) ? d.timeline : [])
  .filter((s) => s && typeof s === "object" && (s.text || s.result))
  .map((s, i) => ({ key: s.step_id || `step-${i}`, text: s.text || "", result: s.result || "" }));

/** The history cause ({cause_id, cases, percent, ...}) a case belongs to, or null. */
export const causeOf = (causes, causeId) => (causeId && Array.isArray(causes) ? causes.find((c) => c && c.cause_id === causeId) : null) || null;

/** The tiles of a cause's track record: confidence (a meter) and solved cases. */
export function trackTiles(cause) {
  if (!cause) return [];
  const percent = clampPercent(cause.percent);
  const cases = count(cause.cases);
  const tiles = [];
  if (percent !== null) tiles.push(tile("confidence", K.confidence.label, `${percent}%`, K.confidence.explain(percent, cases), K.confidence.how, percent));
  tiles.push(tile("cases", K.cases.label, `${cases}`, K.cases.explain(cases), K.cases.how));
  return tiles;
}

/** The tile of what one case adds to its cause, from the history preview ({action, percent_now, percent_after, change_points, cases_after}). */
export function contributionTile(preview) {
  if (!preview || typeof preview !== "object") return null;
  const now = clampPercent(preview.percent_now);
  const after = clampPercent(preview.percent_after);
  if (now === null || after === null) return null;
  const points = isInt(preview.change_points) ? Math.abs(preview.change_points) : Math.abs(now - after);
  const undone = preview.action === "redo";
  const explain = undone ? K.contribution.explainUndone(now, after) : K.contribution.explain(now, after, count(preview.cases_after));
  return tile("contribution", K.contribution.label, K.contribution.value(points), explain, K.contribution.how);
}
